<script setup lang="ts">
import { useTemplateRef } from 'vue';

import DialogModal from '@/components/dialogs/DialogModal.vue';
import UnitsFiltersComponent from '@/components/units/UnitsFilters.vue';
import { useUnit } from '@/composables/useUnit';
import type { Unit } from '@/typings/units/Unit';
import { DefaultUnitsFilters, type UnitsFilters } from '@/typings/units/UnitsFilters';

const { getDefaultUnitFilters } = useUnit();

const isOpen = defineModel<boolean>('open', { default: false });
const filters = defineModel<UnitsFilters>('filters', { default: { ...DefaultUnitsFilters } });
const dialogModalComponent = useTemplateRef<InstanceType<typeof DialogModal>>('dialogElement');

const props = defineProps<{
  allUnits: Unit[];
  resultCount: number;
}>();

defineExpose({
  dialogModalComponent
});

const deleteAllFilters = () => {
  filters.value = getDefaultUnitFilters();
};

const closeModal = () => {
  isOpen.value = false;
};
</script>

<template>
  <DialogModal ref="dialogModalComponent"
               v-model:open="isOpen"
               :hide-close-button="false"
               :show-footer="true"
               class="unitFilterDialogModal">
    <template #header>
      <slot name="header"></slot>
    </template>
    
    <div class="unitFilterDialogModal-contentContainer">
      <UnitsFiltersComponent v-model:filters="filters"
                             :all-units="props.allUnits" />
    </div>
    
    <template #headerContent>
      <slot name="headerContent">
        {{ $t('forms.units.filters.title') }}
      </slot>
    </template>
    
    <template #footerContent>
      <div class="unitFilterDialogModal-actions">
        <button class="btn btn_secondary"
                @click="deleteAllFilters()">
          {{ $t('forms.units.filters.buttons.resetFilters') }}
        </button>
        <button class="btn"
                @click="closeModal()">
          {{ $t('forms.units.filters.buttons.seeResults', props.resultCount) }}
        </button>
      </div>
    </template>
  </DialogModal>
</template>

<style lang="scss">
.unitFilterDialogModal {
  --contentPadding: 1rem;
  --dialogBorderColor: var(--gray-800, oklch(.9 0 0));
  --dialogMaxWidth: 80ch;
  --dialogWidth: min(var(--dialogMaxWidth), 100% - (2 * var(--contentPadding)));

  backdrop-filter: blur(8px);
  border: 1px solid var(--dialogBorderColor);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: var(--dialogWidth);

  .dialogModal-body {
    &:has(.slider-state-drag) {
      overflow-x: hidden;
    }
  }

  @media (width <= 600px) {
    --dialogWidth: 100%;
    --modalMaxHeight: 100vb;
    max-width: 100%;

    .dialogModal-body {
      max-height: fit-content;
    }
  }
}

.unitFilterDialogModal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem var(--padding, 1rem);
  font-weight: 600;
}

.unitFilterDialogModal-heading {
  font-size: 2em;
}

.unitFilterDialogModal-statusBadge {
  --badgeColor: oklch(from var(--baseColor) calc(l * .4) c h);
  --badgeBackgroundColor: oklch(from var(--baseColor) l c h /.3);
  display: inline-block;
  padding: 0.25rem 1rem;
  border: 1px solid currentColor;
  border-radius: 0.25rem;
  font-size: .9em;
  font-weight: 500;
  color: var(--badgeColor);
  background-color: var(--badgeBackgroundColor);
  margin-left: 0.5rem;
}

.unitFilterDialogModal-contentContainer {
  container: dialogContentContainer / inline-size
}

.unitFilterDialogModal-description {
  grid-area: description;

  > * + * {
    margin-top: var(--spc24-16);
  }

  h3 {
    margin-bottom: 0;
  }
}

.unitFilterDialogModal-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spc16-8);

  @media (width <= 600px) {
    flex-direction: column-reverse;
    > * {
      width: 100%;
    }
  }
}
</style>
